// Screens the app loads on demand (the board, Settings, Share, the dealer
// picker…) live in hashed files that each deploy replaces. A tab opened before
// a deploy still asks for the old names: the new service worker has dropped
// them from its cache (skipWaiting + clientsClaim in vite.config.ts) and
// Vercel answers an unknown path with index.html, so the screen fails to load
// and React leaves a blank page. Vite reports each failure as
// `vite:preloadError`; reloading picks up the current deploy, and the game
// survives because it lives in localStorage.
//
// A failure alone doesn't mean a deploy: WebKit can drop a file while a
// service worker takes over, and the worker may still serve an old page.
// Even when the entry is unchanged, reload once per entry to clear a failed
// module from the browser's module map. Don't reload offline or loop forever.
// Each failure is reported with what it led to (errorReporting.ts).

import { reportProblem } from './errorReporting';

const LAST_RELOAD_KEY = 'staleChunkReloadAt';
const RETRIED_ENTRY_KEY = 'staleChunkRetriedEntry';

/** A failure this soon after our own reload isn't fixed by another: show the
 *  error rather than loop. */
const RETRY_AFTER_MS = 10_000;

const ENTRY_SCRIPT = /<script\b[^>]*\btype="module"[^>]*\bsrc="([^"]+)"/;

const runningEntry = () =>
  document.querySelector('script[type="module"][src]')?.getAttribute('src');

async function liveEntry(): Promise<string | undefined> {
  const res = await fetch('/', { cache: 'no-store' });
  return ENTRY_SCRIPT.exec(await res.text())?.[1];
}

// WebKit can cache an HTML response for a script even across a page reload.
// Its import error may omit the URL, so revalidate this page's own JS assets.
async function refreshScripts() {
  try {
    const scripts = new Set(
      performance
        .getEntriesByType('resource')
        .map((entry) => entry.name)
        .filter((name) => {
          const url = new URL(name);
          return (
            url.origin === window.location.origin &&
            /^\/assets\/[^/]+\.js$/.test(url.pathname)
          );
        }),
    );
    await Promise.race([
      Promise.allSettled(
        [...scripts].map((url) => fetch(url, { cache: 'reload' })),
      ),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
  } catch {
    // Resource timing may be unavailable; still attempt the reload.
  }
}

type Outcome = 'reloaded' | 'same_version' | 'check_failed' | 'just_reloaded';

async function reloadIfRedeployed(error: unknown) {
  const report = (outcome: Outcome) =>
    reportProblem('chunk_load_failed', { chunk_outcome: outcome }, error);
  try {
    const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY));
    if (Date.now() - last < RETRY_AFTER_MS) return report('just_reloaded');
    const live = await liveEntry();
    // No entry script: a captive portal's page, say.
    if (!live) return report('check_failed');
    if (live === runningEntry()) {
      if (sessionStorage.getItem(RETRIED_ENTRY_KEY) === live)
        return report('same_version');
      sessionStorage.setItem(RETRIED_ENTRY_KEY, live);
    }
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // Offline, or no record of the last reload (it could loop): stay.
    return report('check_failed');
  }
  await refreshScripts();
  report('reloaded');
  // After the check, so never from inside the failure: WebKit ignored a
  // reload made there.
  window.location.reload();
}

/** Vite's event carries the failed import's error as `payload`. */
const onPreloadError = (event: Event) =>
  void reloadIfRedeployed((event as Event & { payload?: unknown }).payload);

/** Reloads into a newer deploy when an on-demand file fails to load; returns
 *  a function that stops. */
export function reloadOnStaleChunk(): () => void {
  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
